import { z } from 'zod';

import { MILESTONES } from '@/lib/wizard/milestones';
import type { MilestoneId, WizardVariantId } from '@/lib/wizard/wizard';
import { FALLBACK_WIZARD_VARIANT_ID, WIZARD_VARIANTS } from '@/lib/wizard/wizard.config';
import {
  type InsuranceBuilder,
  InsuranceBuilderSchema,
  initialInsuranceBuilderState,
} from '@/types/insurance-builder.type';

const patientRecipientBaseSchema = z.enum(['me', 'someone-else', '']);
const patientGenderBaseSchema = z.enum(['male', 'female', '']);
const representativeRelationshipBaseSchema = z.enum(['family-member', 'non-family-caregiver', '']);
const representativeAuthorizedBaseSchema = z.enum(['yes', 'no', '']);
const WizardVariantIdBaseSchema = z.custom<WizardVariantId>(
  (value): value is WizardVariantId => typeof value === 'string' && value in WIZARD_VARIANTS
);

const MilestoneIdBaseSchema = z
  .enum([
    MILESTONES.CATEGORY_SELECTED,
    MILESTONES.PATIENT_INFO_COLLECTED,
    MILESTONES.INSURANCE_DETERMINED,
    MILESTONES.PROFILE_CREATED,
    MILESTONES.PRODUCTS_SELECTED,
    MILESTONES.APPOINTMENT_RESERVED,
    MILESTONES.PHONE_VERIFIED,
    MILESTONES.ORDER_COMPLETED,
    MILESTONES.WAITLISTED,
    MILESTONES.REACH_OUT_PENDING,
  ])
  .nullable();

export interface SessionStoreState {
  patientRecipient: '' | 'me' | 'someone-else';
  patientFirstName: string;
  patientLastName: string;
  patientDateOfBirth: string;
  patientGender: '' | 'male' | 'female';

  contactPhoneNumber: string;
  contactPhoneNumberVerified: boolean;
  contactEmail: string;
  contactAddressLine1: string;
  contactAddressLine2: string;
  contactCity: string;
  contactState: string;
  contactZip: string;

  representativeFirstName: string;
  representativeLastName: string;
  representativeRelationship: '' | 'family-member' | 'non-family-caregiver';
  representativeAuthorizedForMedicalDecisions: '' | 'yes' | 'no';

  insuranceZipCode: string;
  insuranceBuilder: InsuranceBuilder;
  referrerProductId: string | null;

  selectedCategoryIds: string[];
  selectedProductIds: string[];
  productSelectionConfirmed: boolean;

  schedulingSelectedSlot: string;
  schedulingSelectedProviderId: string;
  schedulingSelectedContactType: string;
  schedulingSelectedAppointmentTypeId: string;

  wizardVariantId: WizardVariantId;
  exitMilestone: MilestoneId | null;
}

export const initialState: SessionStoreState = {
  patientRecipient: '',
  patientFirstName: '',
  patientLastName: '',
  patientDateOfBirth: '',
  patientGender: '',

  contactPhoneNumber: '',
  contactPhoneNumberVerified: false,
  contactEmail: '',
  contactAddressLine1: '',
  contactAddressLine2: '',
  contactCity: '',
  contactState: '',
  contactZip: '',

  representativeFirstName: '',
  representativeLastName: '',
  representativeRelationship: '',
  representativeAuthorizedForMedicalDecisions: '',

  insuranceZipCode: '',
  insuranceBuilder: initialInsuranceBuilderState,
  referrerProductId: null,

  selectedCategoryIds: [],
  selectedProductIds: [],
  productSelectionConfirmed: false,

  schedulingSelectedSlot: '',
  schedulingSelectedProviderId: '',
  schedulingSelectedContactType: '',
  schedulingSelectedAppointmentTypeId: '',

  wizardVariantId: FALLBACK_WIZARD_VARIANT_ID,
  exitMilestone: null,
};

const SessionStoreRecoverySchema = z
  .object({
    patientRecipient: patientRecipientBaseSchema.catch(initialState.patientRecipient),
    patientFirstName: z.string().catch(initialState.patientFirstName),
    patientLastName: z.string().catch(initialState.patientLastName),
    patientDateOfBirth: z.string().catch(initialState.patientDateOfBirth),
    patientGender: patientGenderBaseSchema.catch(initialState.patientGender),

    contactPhoneNumber: z.string().catch(initialState.contactPhoneNumber),
    contactPhoneNumberVerified: z.boolean().catch(initialState.contactPhoneNumberVerified),
    contactEmail: z.string().catch(initialState.contactEmail),
    contactAddressLine1: z.string().catch(initialState.contactAddressLine1),
    contactAddressLine2: z.string().catch(initialState.contactAddressLine2),
    contactCity: z.string().catch(initialState.contactCity),
    contactState: z.string().catch(initialState.contactState),
    contactZip: z.string().catch(initialState.contactZip),

    representativeFirstName: z.string().catch(initialState.representativeFirstName),
    representativeLastName: z.string().catch(initialState.representativeLastName),
    representativeRelationship: representativeRelationshipBaseSchema.catch(
      initialState.representativeRelationship
    ),
    representativeAuthorizedForMedicalDecisions: representativeAuthorizedBaseSchema.catch(
      initialState.representativeAuthorizedForMedicalDecisions
    ),

    insuranceZipCode: z.string().catch(initialState.insuranceZipCode),
    insuranceBuilder: InsuranceBuilderSchema.catch(initialState.insuranceBuilder),
    referrerProductId: z.uuid().nullable().catch(initialState.referrerProductId),

    selectedCategoryIds: z.array(z.string()).catch(initialState.selectedCategoryIds),
    selectedProductIds: z.array(z.string()).catch(initialState.selectedProductIds),
    productSelectionConfirmed: z.boolean().catch(initialState.productSelectionConfirmed),

    schedulingSelectedSlot: z.string().catch(initialState.schedulingSelectedSlot),
    schedulingSelectedProviderId: z.string().catch(initialState.schedulingSelectedProviderId),
    schedulingSelectedContactType: z.string().catch(initialState.schedulingSelectedContactType),
    schedulingSelectedAppointmentTypeId: z
      .string()
      .catch(initialState.schedulingSelectedAppointmentTypeId),

    wizardVariantId: WizardVariantIdBaseSchema.catch(initialState.wizardVariantId),
    exitMilestone: MilestoneIdBaseSchema.catch(initialState.exitMilestone),
  })
  .catch(initialState);

// Safely parse raw data into SessionStoreState - never throws, uses fallbacks
export function parseSessionData(rawData: unknown): SessionStoreState {
  return SessionStoreRecoverySchema.parse(rawData);
}
